import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { formatDateTime } from '../utils/formatDate';
import type { MissionParticipant } from '../api/interventionRequestsApi';
import { initials } from './rating/RatingForm';

/**
 * The Jira field « Participants à la demande », said for what it is (D50).
 *
 * It was rendered under the label « Technicien », which is false: it is an **access list**, and it
 * carries L'Oréal ECMs and DRVs beside our technicians. GPS marks the ones it recognises, removes
 * nobody, and offers no control — the list is managed in Jira.
 */

/**
 * Where this person comes from, and only what we actually know. A participant our changelog never
 * saw arrive is said to be from Jira without an author, rather than given an invented one.
 */
const provenance = (
  t: (key: string, options?: Record<string, unknown>) => string,
  participant: MissionParticipant
): string => {
  if (participant.technicianUserId !== null && participant.solicited) {
    return t('Solicited through GPS on this ticket');
  }
  const unsolicited = participant.technicianUserId !== null;
  if (participant.addedByName && participant.addedAt) {
    const added = t('Added in Jira by {{author}} on {{date}}', {
      author: participant.addedByName,
      date: formatDateTime(participant.addedAt),
    });
    return unsolicited ? `${added} · ${t('no GPS solicitation')}` : added;
  }
  return unsolicited
    ? t('Present in Jira, with no GPS solicitation')
    : t('Added in Jira');
};

interface RequestParticipantsCardProps {
  participants: MissionParticipant[];
}

export const RequestParticipantsCard = ({ participants }: RequestParticipantsCardProps) => {
  const { t } = useTranslation();
  const [explained, setExplained] = useState(false);

  if (participants.length === 0) return null;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 sm:p-6">
      <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-2 dark:border-zinc-800">
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100 sm:text-lg">
          {t('Request participants')}
        </h3>
        {/* The only control on this card, and it explains rather than acts. */}
        <button
          type="button"
          aria-label={t('Why non-technicians appear here')}
          aria-expanded={explained}
          onClick={() => setExplained((open) => !open)}
          className="text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-200"
        >
          <Info className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="ml-auto rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:bg-zinc-800 dark:text-zinc-300">
          {participants.length}
        </span>
      </div>

      {explained && (
        <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-zinc-400">
          {t(
            "This ticket's access list in Jira. GPS adds the technician who accepted; L'Oréal ECMs and DRVs add themselves to follow and comment the ticket. It does not say who carries out the intervention."
          )}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {participants.map((participant) => (
          <li key={participant.accountId} className="flex items-start gap-3">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-semibold text-gray-700 dark:bg-zinc-800 dark:text-zinc-300"
              aria-hidden="true"
            >
              {initials(participant.displayName)}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm text-gray-900 dark:text-zinc-100">
                  {participant.displayName}
                </span>
                {/* Marked only where we know it. A participant with no GPS account carries no
                    badge at all — inventing « ECM » or « DRV » would be a claim Jira never made. */}
                {participant.technicianUserId !== null && (
                  <span className="rounded-md bg-violet-50 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-950 dark:text-violet-300">
                    {t('Technician')}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-zinc-400">
                {provenance(t, participant)}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-zinc-800 dark:text-zinc-400">
        {t(
          "Read only: GPS adds the technician who accepts; ECMs and DRVs add and remove themselves in Jira."
        )}
      </p>
    </section>
  );
};
